import { Typography } from '@euler/app/components/typography';
import { ProductItemCell } from '@euler/app/flows/products/components/ProductItemCell';
import { ProductManifestProps } from '@euler/app/flows/report/types';
import { deriveManifestItemAdopter } from '@euler/functions/product/deriveManifestItemAdopter';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ReportProductReferralItemConfirmation } from '@euler/model/report';
import { TaskProductManifestItem } from '@euler/model/task-detail';
import { idEquals } from '@euler/utils';
import { useCreation, useLocalObservable } from '@euler/utils/hooks';
import { memo } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import { BehaviorSubject, map } from 'rxjs';
import { useTheme } from 'styled-components';

export const ConstructionReportJobProductList = memo(
  ({
    jobId,
    jobName,
    productManifest,
    productConfirmations,
    style,
  }: {
    jobId: number;
    jobName: string;
    productManifest?: ProductManifestProps;
    productConfirmations: ReportProductReferralItemConfirmation[] | undefined;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();

    const defaultProductManifest$ = useCreation(
      () => new BehaviorSubject<TaskProductManifestItem[]>([]),
      [],
    );

    const productManifestItems = useLocalObservable(
      () =>
        (productManifest?.items$ ?? defaultProductManifest$).pipe(
          map(list =>
            list.filter(
              x => x.subject === jobName && x.jobRefs?.includes(jobId),
            ),
          ),
        ),
      [],
    );

    if (!productManifestItems.length) {
      return null;
    }

    return (
      <View style={style}>
        <Typography.Label
          medium
          size={rpx(16)}
          lineHeight={rpx(20)}
          color={theme.report.construction.procedure.titleColor}
          css={`
            margin-bottom: ${rpx(5)}px;
          `}
        >
          产品信息
        </Typography.Label>
        {productManifestItems.map(item => (
          <ProductItemCell
            key={item.id}
            item={item}
            name={item.product.name}
            title={item.product.title}
            qty={item.qty}
            coverUrl={item.product.coverUrl}
            showBadge={true}
            removable={false}
            adopted={
              item.jobRefs?.includes(jobId)
                ? true
                : productConfirmations?.find(
                    x =>
                      idEquals(x.contractId, item.product.sourceContractId) &&
                      idEquals(x.productId, item.product.id),
                  )?.adopted
            }
            author={item.author}
            adopter={deriveManifestItemAdopter(item, jobId)}
            closer={item.closer ?? undefined}
            onPress={productManifest?.onViewDetail}
            css={`
              margin: ${rpx(1)}px 0;
            `}
          />
        ))}
      </View>
    );
  },
);
